// Goalie materials — the save-focused counterpart to the skater shell.
// Silhouette rule from the art bible carries over: skaters are translucent
// hollow-reading shells, goalies are SOLID bright domes. Only two goalies
// exist, so these are plain per-team materials with uniforms (no
// instancing); the pulled-state dim and save-ring mode alpha are per-frame
// uniform writes.

import { mix, normalView, positionViewDirection, smoothstep, uniform, uv } from "three/tsl";
import { Color, DoubleSide, MeshBasicNodeMaterial } from "three/webgpu";
import { TEAM_ENTITY_COLORS } from "../style/palette.js";
import type { EntitiesStyle } from "../style/style-schema.js";

function buildUniforms(style: EntitiesStyle, team: "home" | "away") {
  const colors = TEAM_ENTITY_COLORS[team];
  return {
    core: uniform(new Color(colors.core)),
    glow: uniform(new Color(colors.glow)),
    rim: uniform(new Color(colors.rim)),
    domeIntensity: uniform(style.goalie.domeIntensity),
    shieldIntensity: uniform(style.goalie.shieldIntensity),
    saveRingColor: uniform(new Color(style.goalie.saveRingColor)),
    saveRingIntensity: uniform(style.goalie.saveRingIntensity),
    /** Per-frame: save-ring alpha by goalie mode. */
    saveRingAlpha: uniform(style.goalie.saveRingAlphaReady),
    /** Per-frame: 1 normal, pulledDim when pulled. */
    dim: uniform(1),
  };
}

export type GoalieUniforms = ReturnType<typeof buildUniforms>;

export function applyGoalieStyle(u: GoalieUniforms, s: EntitiesStyle): void {
  u.domeIntensity.value = s.goalie.domeIntensity;
  u.shieldIntensity.value = s.goalie.shieldIntensity;
  u.saveRingColor.value.set(s.goalie.saveRingColor);
  u.saveRingIntensity.value = s.goalie.saveRingIntensity;
}

export function createGoalieMaterials(style: EntitiesStyle, team: "home" | "away") {
  const u = buildUniforms(style, team);

  // Solid dome: opaque, core-bright with rim pop — clearly not a skater shell.
  const dome = new MeshBasicNodeMaterial();
  {
    const fres = normalView.dot(positionViewDirection).abs().oneMinus();
    dome.colorNode = mix(u.core, u.rim, fres.mul(fres)).mul(u.domeIntensity).mul(u.dim);
  }

  // Shield arc: hot emissive band, rotated to the blocking direction.
  const shield = new MeshBasicNodeMaterial();
  shield.colorNode = mix(u.core, u.rim, 0.5).mul(u.shieldIntensity).mul(u.dim);

  // Save-volume ring decal (unit quad, annulus at the rim).
  const saveRing = new MeshBasicNodeMaterial({
    transparent: true,
    depthWrite: false,
    side: DoubleSide,
  });
  {
    const r = uv().sub(0.5).mul(2).length();
    const annulus = smoothstep(0.02, 0.0, r.sub(0.97).abs().sub(0.015));
    saveRing.colorNode = u.saveRingColor.mul(u.saveRingIntensity);
    saveRing.opacityNode = annulus.mul(u.saveRingAlpha);
  }

  return { u, dome, shield, saveRing };
}
